/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFormValues, change } from 'redux-form';
import {
  Layout,
  Card,
  TextField,
  Stack,
  Button,
  SkeletonDisplayText,
  Icon,
  TextStyle,
  Banner
} from '@shopify/polaris';
import { SearchMinor, CancelSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { checkValidateStopWord } from 'src/helpers/stop-words-helper';

import empty from 'src/assets/images/filter-tree/empty-option.png';

type Props = {
  stopWords: any[],
  loading: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = { searchValue: string, addValue: string, activeErrorBanner: boolean, error: string };
class StopWordsList extends Component<ComposedProps, State> {
  state = {
    searchValue: '',
    addValue: '',
    activeErrorBanner: false,
    error: ''
  };

  render() {
    const { i18n, stopWords, loading } = this.props;
    const { searchValue, addValue, activeErrorBanner, error } = this.state;

    const validStopWords = stopWords.filter(item =>
      _.isEmpty(searchValue) ||
      item.value.includes(searchValue) ||
      item.isNew
    )

    return (
      <React.Fragment>
        <Layout.Section>
          {activeErrorBanner && (
            <Banner
              title={i18n.translate(error)}
              status="critical"
              onDismiss={this.handleDismissErrorBanner}></Banner>
          )}
        </Layout.Section>
        <Layout.Section>
          <Card>
            <Card.Section>
              <div className='BC__Search-header'>
                  <TextStyle variation='strong'>
                    {i18n.translate('AdminHTML.StopWords.StopWords.tableTitle')}
                  </TextStyle>
                  <div className="BC__StopWords-Search">
                    <TextField
                      value={searchValue}
                      onChange={this.handleChangeSearchValue}
                      placeholder={i18n.translate('AdminHTML.StopWords.StopWords.inputSearchText')}
                      prefix={<Icon source={SearchMinor} color="base" />}
                    />
                  </div>
              </div>
            </Card.Section>
            <Card.Section>
              <div className="BC__StopWords-Add">
                <Stack distribution="fillEvenly">
                  <div onKeyDown={this.handleKeyPress}>
                    <TextField
                      value={addValue}
                      onChange={this.handleChangeAddValue}
                      placeholder={i18n.translate(
                        'AdminHTML.StopWords.StopWords.stopWord_Placeholder'
                      )}
                    />
                  </div>
                  <Button primary onClick={this.handleAddValue}>
                    {i18n.translate('AdminHTML.Common.Action.add')}
                  </Button>
                </Stack>
              </div>
              {loading ? (
                <ul className="BC__StopWords-List">
                  {[...Array(5).keys()].map((item, key) => (
                    <li className="BC__StopWords-Item" key={key}>
                      <SkeletonDisplayText size="small" />
                    </li>
                  ))}
                </ul>
              ) : (
                !_.isEmpty(validStopWords) && (
                  <ul className="BC__StopWords-List">
                    {validStopWords.map((item, key) => (
                        <li className={`BC__StopWords-Item ${item.isNew && 'is-new'}`} key={key}>
                          <div className="BC__StopWords--Value">
                            <TextStyle variation="subdued">{item.value}</TextStyle>
                          </div>
                          <div className="BC__StopWords--Remove">
                            <Button
                              plain
                              onClick={() => this.handleDeleteValue(key)}
                              icon={CancelSmallMinor}
                            />
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                )
              )}

              {_.isEmpty(validStopWords) && !loading && (
                <div className="BC-EmptyOption__StopWords">
                  <img src={empty} alt="Empty" />
                  <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
                    {i18n.translate(`AdminHTML.StopWords.StopWords.${this.state.searchValue ? 'notFoundTitle' : 'emptyTitle'}`)}
                  </p>
                  <TextStyle variation="subdued">
                    {i18n.translate(`AdminHTML.StopWords.StopWords.${this.state.searchValue ? 'notFoundContent' : 'noStopWords'}`)}
                  </TextStyle>
                </div>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>
      </React.Fragment>
    );
  }

  // Handle change search value
  handleChangeSearchValue = value => {
    this.setState({
      searchValue: value
    });
  };

  // Handle change add value
  handleChangeAddValue = value => {
    this.setState({
      addValue: value
    });
  };

  // Handle add stop word
  handleAddValue = () => {
    const { fields, stopWords } = this.props;
    let { addValue } = this.state;

    const checkedValue: Object = checkValidateStopWord(addValue, stopWords);
    if (checkedValue.error) {
      this.setState({
        activeErrorBanner: true,
        error: checkedValue.error
      });
    } else {
      const { stopWord = '' } = checkedValue;
      if (stopWord) {
        fields.unshift({ value: stopWord, isNew: true });
      }
      this.setState({
        addValue: '',
        activeErrorBanner: false,
        error: ''
      });
    }

  };

  // Handle when the enter key is press in Add input
  handleKeyPress = event => {
    const enterKeyPressed = event.keyCode === 13;
    if (enterKeyPressed) {
      event.preventDefault();
      this.handleAddValue();
    }
  };

  // Handle dismiss error banner
  handleDismissErrorBanner = () => {
    this.setState({
      activeErrorBanner: false
    });
  };

  // Handle click delete button
  handleDeleteValue = index => {
    const { fields } = this.props;

    fields.remove(index);
  };
}

const mapStateToProps = (state, props) => {
  const { stopWords } = state;
  const formValues = getFormValues('StopWordsForm')(state);

  return {
    stopWords: formValues && formValues.stopWords ? formValues.stopWords : {},
    loading: stopWords.getLoading
  };
};

const mapDispatchToProps = dispatch => ({
  updateStopWords: (index, data) => dispatch(change(`StopWordsForm`, `stopWords[${index}]`, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(StopWordsList));
